import { GameObject, Vector2, Rectangle } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';

export enum AsteroidSize {
  LARGE = 'large',
  MEDIUM = 'medium',  
  SMALL = 'small'
}

export class Asteroid implements GameObject {
  public id: string;
  public position: Vector2;
  public velocity: Vector2 = { x: 0, y: 0 };
  public size: Vector2;
  public active: boolean = true;
  public radius: number;

  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private asteroidSize: AsteroidSize;
  private rotationAngle: number = 0;
  private rotationSpeed: number;
  private gameWidth: number;
  private gameHeight: number;
  private shape: Vector2[] = []; // Irregular asteroid shape points

  // Point values for different sizes (from official Atari manual)
  private static readonly POINT_VALUES = {
    [AsteroidSize.LARGE]: 20,
    [AsteroidSize.MEDIUM]: 50,
    [AsteroidSize.SMALL]: 100
  };

  // Size properties
  private static readonly SIZE_PROPERTIES = {
    [AsteroidSize.LARGE]: { radius: 32, minSpeed: 20, maxSpeed: 50 },
    [AsteroidSize.MEDIUM]: { radius: 20, minSpeed: 30, maxSpeed: 80 },
    [AsteroidSize.SMALL]: { radius: 12, minSpeed: 50, maxSpeed: 120 }
  };

  constructor(
    id: string,
    x: number,
    y: number,
    asteroidSize: AsteroidSize,
    renderManager: RenderManager,
    audioManager: AudioManager,
    gameWidth: number,
    gameHeight: number,
    velocityX?: number,
    velocityY?: number
  ) {
    this.id = id;
    this.position = { x, y };
    this.asteroidSize = asteroidSize;
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    this.gameWidth = gameWidth;
    this.gameHeight = gameHeight;

    const sizeProps = Asteroid.SIZE_PROPERTIES[asteroidSize];
    this.radius = sizeProps.radius;
    this.size = { x: this.radius * 2, y: this.radius * 2 };

    // Set velocity
    if (velocityX !== undefined && velocityY !== undefined) {
      this.velocity = { x: velocityX, y: velocityY };
    } else {
      this.generateRandomVelocity();
    }

    // Random rotation speed
    this.rotationSpeed = (Math.random() - 0.5) * 180; // -90 to 90 degrees per second

    // Generate irregular shape
    this.generateShape();
  }

  private generateRandomVelocity(): void {
    const sizeProps = Asteroid.SIZE_PROPERTIES[this.asteroidSize];
    const speed = sizeProps.minSpeed + Math.random() * (sizeProps.maxSpeed - sizeProps.minSpeed);
    const angle = Math.random() * Math.PI * 2;
    
    this.velocity = {
      x: Math.cos(angle) * speed,
      y: Math.sin(angle) * speed
    };
  }

  private generateShape(): void {
    const baseRadius = this.radius;
    const points = 8 + Math.floor(Math.random() * 4); // 8-11 points for irregular shape
    this.shape = [];

    for (let i = 0; i < points; i++) {
      const angle = (i / points) * Math.PI * 2;
      // Vary the radius by 20-40% to make it irregular
      const radiusVariation = 0.6 + Math.random() * 0.4;
      const radius = baseRadius * radiusVariation;
      
      this.shape.push({
        x: Math.cos(angle) * radius,
        y: Math.sin(angle) * radius
      });
    }
  }

  public update(deltaTime: number): void {
    // Update rotation
    this.rotationAngle += this.rotationSpeed * deltaTime;

    // Update position
    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;

    // Wraparound screen edges
    this.wrapAroundScreen();
  }

  private wrapAroundScreen(): void {
    const halfSize = this.radius;
    
    if (this.position.x < -halfSize) {
      this.position.x = this.gameWidth + halfSize;
    } else if (this.position.x > this.gameWidth + halfSize) {
      this.position.x = -halfSize;
    }

    if (this.position.y < -halfSize) {
      this.position.y = this.gameHeight + halfSize;
    } else if (this.position.y > this.gameHeight + halfSize) {
      this.position.y = -halfSize;
    }
  }

  public render(ctx: CanvasRenderingContext2D): void {
    if (!this.active) return;

    const centerX = this.position.x;
    const centerY = this.position.y;
    const angleRad = this.rotationAngle * (Math.PI / 180);

    // Transform shape points with rotation
    const transformedPoints = this.shape.map(point => {
      const rotX = point.x * Math.cos(angleRad) - point.y * Math.sin(angleRad);
      const rotY = point.x * Math.sin(angleRad) + point.y * Math.cos(angleRad);
      return { x: centerX + rotX, y: centerY + rotY };
    });

    // Draw asteroid outline
    this.renderManager.drawPolygon(transformedPoints, '#FFFFFF', false);
  }

  public split(): Asteroid[] {
    const fragments: Asteroid[] = [];

    if (this.asteroidSize === AsteroidSize.LARGE) {
      // Large asteroid splits into 2 medium asteroids
      for (let i = 0; i < 2; i++) {
        const angle = (Math.PI / 3) * i + Math.random() * Math.PI / 2; // Spread fragments
        const speed = 60 + Math.random() * 40;
        
        fragments.push(new Asteroid(
          `${this.id}_fragment_${i}`,
          this.position.x,
          this.position.y,
          AsteroidSize.MEDIUM,
          this.renderManager,
          this.audioManager,
          this.gameWidth,
          this.gameHeight,
          Math.cos(angle) * speed,
          Math.sin(angle) * speed
        ));
      }
    } else if (this.asteroidSize === AsteroidSize.MEDIUM) {
      // Medium asteroid splits into 2 small asteroids
      for (let i = 0; i < 2; i++) {
        const angle = (Math.PI / 2) * i + Math.random() * Math.PI / 2;
        const speed = 80 + Math.random() * 60;
        
        fragments.push(new Asteroid(
          `${this.id}_fragment_${i}`,
          this.position.x,
          this.position.y,
          AsteroidSize.SMALL,
          this.renderManager,
          this.audioManager,
          this.gameWidth,
          this.gameHeight,
          Math.cos(angle) * speed,
          Math.sin(angle) * speed
        ));
      }
    }
    // Small asteroids don't split further

    // Destroy this asteroid
    this.destroy();

    return fragments;
  }

  public getPointValue(): number {
    return Asteroid.POINT_VALUES[this.asteroidSize];
  }

  public getSize(): AsteroidSize {
    return this.asteroidSize;
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x - this.radius,
      y: this.position.y - this.radius,
      width: this.radius * 2,
      height: this.radius * 2
    };
  }

  public destroy(): void {
    this.active = false;
  }
}